"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Contact() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <section id="contact" ref={sectionRef} className="bg-background py-24 px-8">
      <div className="max-w-lg mx-auto">
        <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="font-display text-foreground text-4xl md:text-5xl text-center mb-4">
            Contact Us
          </h2>
        </div>

        <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-100 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Separator className="bg-border w-24 mx-auto mb-6" />
        </div>

        <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-200 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          
        </div>

        <FormProvider className="space-y-6" formId="contact_form_home" sectionName="contact-section" defaultValues={{
        name: "",
        email: "",
        practice_name: "",
        phone: "",
        message: "",
        email_signup: false
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        practice_name: {
          required: true
        },
        phone: {
          pattern: "phone"
        },
        message: {
          required: true
        }
      }} submitText="SEND" successMessage="Thank you! We'll be in touch soon to discuss your practice diagnostic." errorMessage="Failed to send your message. Please try again.">
          <ContactFormFields sectionVisible={sectionVisible} />
        </FormProvider>
      </div>
    </section>;
}
function ContactFormFields({
  sectionVisible
}: {
  sectionVisible: boolean;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary/10 border border-primary rounded-sm text-center animate-fade-in-up">
        <p className="text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-[300ms] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary" {...field} />
              </FormControl>
              
              <FormMessage />
            </FormItem>} />
      </div>

      <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-[400ms] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-[500ms] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="practice_name" rules={{
        required: "Practice name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Practice Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Clinic or Practice" className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-[600ms] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary" {...field} />
              </FormControl>
              
              <FormMessage />
            </FormItem>} />
      </div>

      <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-[700ms] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="message" rules={{
        required: "Message is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your practice and what you hope to achieve..." className="bg-transparent border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary min-h-32" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-[800ms] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="email_signup" render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
              </FormControl>
              <FormLabel className="text-muted-foreground font-normal text-sm leading-tight">
                Sign up for our email list for updates, promotions, and more.
              </FormLabel>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500 rounded-sm animate-fade-in-up">
          <p className="text-red-500 text-sm">{submitError}</p>
        </div>}

      <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-[900ms] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <Button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-9 has-[>svg]:px-3 w-full text-foreground-primary border border-primary rounded-sm py-3 px-6 font-semibold uppercase transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring !bg-[hsl(29,100%,50%)] disabled:pointer-events-none dark:aria-invalid:ring-destructive/40 hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed">
          {isSubmitting ? <>
              <Loader className="animate-spin size-4" />
              Sending...
            </> : "SEND"}
        </Button>
      </div>

      <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-[1000ms] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <p className="text-muted-foreground text-xs text-center mt-4">
          This site is protected by reCAPTCHA and the Google Privacy Policy and
          Terms of Service apply.
        </p>
      </div>
    </Form>;
}